import { SharedSpinner } from '../../Loading/SharedSpinner';
import { GREEN } from '../../Theme/palette';
import { CheckOutlined, CloseOutlined, FileSearchOutlined } from '@ant-design/icons';
import { DrawerCloseButton, SharedEmpty } from '@components';
import { useInspectionTenantCategories, useView } from '@hooks';
import { L } from '@lib';
import { InspectionCategory } from '@services';
import { Button, Drawer, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  /** Categories already on the job - listed, but marked and not addable a second time. */
  addedIds: number[];
  onClose: () => void;
  onAdd: (category: InspectionCategory) => Promise<void>;
}

/**
 * Picks inspection categories for a job, the way the service picker picks work. Each tap adds one
 * straight away and the drawer stays open, so a job that needs three checks is three taps and Done.
 */
export const InspectionPickerDrawer: React.FC<IProps> = observer(
  ({ open, addedIds, onClose, onAdd }) => {
    const { isMobile } = useView();
    const { dataSource, loading } = useInspectionTenantCategories();
    const [addingId, setAddingId] = useState<number | null>(null);

    const handleAdd = async (category: InspectionCategory) => {
      if (addingId !== null || addedIds.includes(category.id)) return;
      setAddingId(category.id);
      try {
        await onAdd(category);
      } finally {
        setAddingId(null);
      }
    };

    const renderItem = (category: InspectionCategory) => {
      const added = addedIds.includes(category.id);
      const adding = addingId === category.id;
      return (
        <div
          key={category.id}
          onClick={() => handleAdd(category)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            cursor: added ? 'default' : 'pointer',
            background: added ? GREEN.soft : 'transparent',
            borderBottom: '1px solid #f5f5f5',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            if (!added) (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
          }}
          onMouseLeave={(e) => {
            if (!added) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
          }}
        >
          <FileSearchOutlined style={{ color: added ? GREEN.badge : '#8c8c8c', fontSize: 16 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontWeight: 500, color: '#333', overflowWrap: 'anywhere' }}>
              {category.name}
            </Text>
          </div>
          {adding ? (
            <SharedSpinner size="small" loading />
          ) : added ? (
            <Text type="success" style={{ fontSize: 12, display: 'inline-flex', gap: 4 }}>
              <CheckOutlined /> {L('Added')}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {L('Add')}
            </Text>
          )}
        </div>
      );
    };

    return (
      <Drawer
        title={L('Add inspection')}
        open={open}
        onClose={onClose}
        placement="right"
        width={isMobile ? '100%' : 480}
        destroyOnHidden
        closable={false}
        extra={<DrawerCloseButton icon={<CloseOutlined />} onClick={onClose} />}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {addedIds.length} {L('on this job')}
            </Text>
            <Button type="primary" onClick={onClose}>
              {L('Done')}
            </Button>
          </div>
        }
      >
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && dataSource.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <SharedSpinner loading />
            </div>
          ) : dataSource.length === 0 ? (
            <SharedEmpty description={L('No inspection categories set up for this garage')} />
          ) : (
            dataSource.map(renderItem)
          )}
        </div>
      </Drawer>
    );
  }
);
